import { useClerk, useReverification } from "@clerk/tanstack-react-start";
import { zodResolver } from "@hookform/resolvers/zod";
import { createFileRoute } from "@tanstack/react-router";
import { Check, Eye, EyeOff, Loader2 } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { Button } from "~/components/ui/button";
import { Card } from "~/components/ui/card";
import { FormField } from "~/components/ui/form-field";
import { Input } from "~/components/ui/input";
import { getClerkErrorMessage } from "~/lib/clerk-errors";
import { cn } from "~/lib/cn";
import { createNoIndexMeta } from "~/lib/shared/meta";
import { validateRedirectPath } from "~/lib/shared/post-auth-routing";

const PasswordSchema = z
  .object({
    password: z.string().min(8, "Password must be at least 8 characters"),
    password2: z.string().min(8, "Password must be at least 8 characters"),
  })
  .refine((d) => d.password === d.password2, {
    path: ["password2"],
    message: "Passwords do not match",
  });

type PasswordForm = z.infer<typeof PasswordSchema>;

const PasswordPageSearchSchema = z
  .object({
    message: z.string().optional(),
    redirectTo: z
      .string()
      .optional()
      .transform((path) =>
        path ? (validateRedirectPath(path) ?? undefined) : undefined,
      ),
  })
  .optional()
  .transform((value) => value ?? {});

type PasswordPageSearch = z.infer<typeof PasswordPageSearchSchema>;

type PageStatus = "idle" | "saving" | "success" | "redirecting";

export const Route = createFileRoute("/_user/settings/password/")({
  staticData: { title: "Password" },
  validateSearch: (search) => PasswordPageSearchSchema.parse(search),
  head: () =>
    createNoIndexMeta(
      "Set Password – Dream Coach Match",
      "Set or update your account password.",
    ),
  component: PasswordSettingsPage,
});

function PasswordSettingsPage() {
  const { user } = useClerk();
  const search: PasswordPageSearch = Route.useSearch();
  const [status, setStatus] = useState<PageStatus>("idle");
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const isSummitRedirect = search.redirectTo?.startsWith("/summit/");
  const isProcessing = status === "saving";
  const isSuccess = status === "success";
  const isRedirecting = status === "redirecting";

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    reset,
  } = useForm<PasswordForm>({
    resolver: zodResolver(PasswordSchema),
    defaultValues: { password: "", password2: "" },
    mode: "onChange",
  });

  const password = watch("password");
  const password2 = watch("password2");
  const hasMinLength = password.length >= 8;
  const passwordsMatch = password.length > 0 && password === password2;

  const updatePassword = useReverification(async (newPassword: string) => {
    if (!user) {
      throw new Error("You must be signed in to update password");
    }
    await user.updatePassword({ newPassword });
  });

  const onSubmit = async (data: PasswordForm) => {
    setStatus("saving");
    setError(null);

    try {
      const result = await updatePassword(data.password);
      // useReverification returns null if user cancels the verification modal
      if (result === null) {
        setStatus("idle");
        return;
      }

      reset();

      if (search.redirectTo) {
        setStatus("success");
        // Let user see the success state before redirecting
        await new Promise((resolve) => setTimeout(resolve, 1500));
        setStatus("redirecting");
        if (typeof window !== "undefined") {
          window.location.replace(search.redirectTo);
        }
      } else {
        setStatus("success");
        // Show success briefly, then return to idle
        await new Promise((resolve) => setTimeout(resolve, 2000));
        setStatus("idle");
      }
    } catch (error) {
      setStatus("idle");
      setError(getClerkErrorMessage(error, "Failed to update password"));
    }
  };

  // Success + Redirecting state
  if (isSuccess || isRedirecting) {
    return (
      <Card
        className={cn(
          "mx-auto w-full max-w-screen-sm items-center text-center transition-opacity duration-300",
          "border-green-200 bg-green-50",
        )}
      >
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
          {isRedirecting ? (
            <Loader2 className="h-8 w-8 animate-spin text-green-600" />
          ) : (
            <Check className="h-8 w-8 text-green-600" />
          )}
        </div>
        <div className="space-y-2">
          <h2 className="font-heading text-xl font-medium text-green-900">
            {isRedirecting ? "Taking you to the Summit..." : "Password saved!"}
          </h2>
          <p className="text-sm text-green-700">
            {isRedirecting
              ? "Redirecting to the Summit Agenda"
              : "Your account is now secured."}
          </p>
        </div>
      </Card>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="max-w-4xl">
      <Card>
        {/* Header - contextual based on destination */}
        <div className="space-y-1">
          {isSummitRedirect ? (
            <>
              <h2 className="font-heading text-xl font-medium text-gray-900">
                One last step before the Summit!
              </h2>
              <p className="text-text-secondary text-sm">
                Create a password to secure your account and access the agenda.
              </p>
            </>
          ) : (
            <>
              <h2 className="font-heading text-xl font-medium text-gray-900">
                Set your password
              </h2>
              <p className="text-text-secondary text-sm">
                Create or update your password for email-based sign in.
              </p>
            </>
          )}
        </div>

        {/* Info message from redirect */}
        {search.message && !isSummitRedirect && (
          <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
            {search.message}
          </div>
        )}

        {/* Password requirements */}
        <div className="space-y-1 text-sm">
          <div
            className={cn(
              "flex items-center gap-2 transition-colors",
              hasMinLength ? "text-green-600" : "text-text-tertiary",
            )}
          >
            {hasMinLength ? (
              <Check className="h-4 w-4" />
            ) : (
              <div className="h-4 w-4 rounded-full border border-current" />
            )}
            At least 8 characters
          </div>
          {password2.length > 0 && (
            <div
              className={cn(
                "flex items-center gap-2 transition-colors",
                passwordsMatch ? "text-green-600" : "text-red-500",
              )}
            >
              {passwordsMatch ? (
                <Check className="h-4 w-4" />
              ) : (
                <div className="h-4 w-4 rounded-full border border-current" />
              )}
              Passwords match
            </div>
          )}
        </div>

        {/* Password field */}
        <FormField
          fullWidth
          label="New password"
          error={errors.password?.message || error || undefined}
        >
          <div className="relative">
            <Input
              {...register("password")}
              type={showPassword ? "text" : "password"}
              autoComplete="new-password"
              autoFocus
              disabled={isProcessing}
              className="pr-10"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute top-1/2 right-3 -translate-y-1/2 text-gray-500 hover:text-gray-700"
              tabIndex={-1}
            >
              {showPassword ? (
                <EyeOff className="h-4 w-4" />
              ) : (
                <Eye className="h-4 w-4" />
              )}
            </button>
          </div>
        </FormField>

        {/* Confirm password field */}
        <FormField
          fullWidth
          label="Confirm password"
          error={errors.password2?.message || undefined}
        >
          <div className="relative">
            <Input
              {...register("password2")}
              type={showConfirmPassword ? "text" : "password"}
              autoComplete="new-password"
              disabled={isProcessing}
              className="pr-10"
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute top-1/2 right-3 -translate-y-1/2 text-gray-500 hover:text-gray-700"
              tabIndex={-1}
            >
              {showConfirmPassword ? (
                <EyeOff className="h-4 w-4" />
              ) : (
                <Eye className="h-4 w-4" />
              )}
            </button>
          </div>
        </FormField>

        {/* Destination preview for summit */}
        {isSummitRedirect && (
          <div className="rounded-2xl border border-purple-200 bg-purple-50 p-4 text-sm text-purple-800">
            After saving, you'll be taken to the Summit Agenda.
          </div>
        )}

        {/* Submit button */}
        <Button
          type="submit"
          loading={isProcessing}
          disabled={isProcessing}
          variant="primary"
          className="w-full"
        >
          {isProcessing
            ? "Saving..."
            : isSummitRedirect
              ? "Save & continue to Summit"
              : "Save password"}
        </Button>
      </Card>
    </form>
  );
}
